import React, { useEffect, useMemo, useState } from 'react';

const DEFAULT_STEPS = [
  'Choose your Specialty',
  'Complete your MedMap Account',
  'Set up Pathfinder',
  'Achieve with Pathfinder',
];

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const OnboardingProgressBar = ({
  currentStep = 1, // 1-based
  steps = DEFAULT_STEPS,
  className = '',
}) => {
  const [animateTo, setAnimateTo] = useState(0);

  const stepCount = steps.length;
  const stepIndex = clamp(Number(currentStep) || 1, 1, stepCount);

  const percent = useMemo(() => {
    if (stepCount <= 1) return 100;
    return Math.round(((stepIndex - 1) / (stepCount - 1)) * 100);
  }, [stepIndex, stepCount]);

  useEffect(() => {
    // Start from 0, then animate to the target after mount/step change
    setAnimateTo(0);
    const raf = requestAnimationFrame(() => setAnimateTo(percent));
    return () => cancelAnimationFrame(raf);
  }, [percent]);

  return (
    <div className={`obp-wrap ${className}`} aria-label="Onboarding progress">
      <div className="obp-shell">
        <div className="obp-head">
          <span className="obp-kicker">Setting you Up</span>
          <span className="obp-meta">
            Step {stepIndex} of {stepCount}
          </span>
        </div>

        <div className="obp-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={animateTo}>
          <div className="obp-fill" style={{ width: `${animateTo}%` }} />
        </div>

        <div className="obp-steps" role="list" aria-label="Onboarding steps">
          {steps.map((label, i) => {
            const s = i + 1;
            const isDone = s < stepIndex;
            const isActive = s === stepIndex;
            return (
              <div key={label} className={`obp-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`} role="listitem">
                <span className="obp-dot" aria-hidden="true" />
                <span className="obp-label">{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .obp-wrap {
          width: 100%;
          display: flex;
          justify-content: center;
          margin: 0 0 1.25rem;
        }

        .obp-shell {
          width: min(1200px, 100%);
          background: linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.78));
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 16px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          backdrop-filter: blur(14px);
          padding: 0.9rem 1rem 0.85rem;
        }

        .obp-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 0.65rem;
        }

        .obp-kicker {
          font-size: 0.75rem;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #475569;
        }

        .obp-meta {
          font-size: 0.85rem;
          font-weight: 600;
          color: #0f172a;
        }

        .obp-track {
          height: 10px;
          border-radius: 999px;
          background: rgba(226, 232, 240, 0.9);
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.35);
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06);
        }

        .obp-fill {
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #3b82f6, #7c3aed);
          box-shadow: 0 10px 22px rgba(59, 130, 246, 0.22);
          transition: width 700ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: width;
        }

        .obp-steps {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.65rem;
          margin-top: 0.75rem;
        }

        .obp-step {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.45rem 0.6rem;
          border-radius: 999px;
          border: 1px solid rgba(226, 232, 240, 0.9);
          background: rgba(248, 250, 252, 0.75);
          min-width: 0;
        }

        .obp-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: #cbd5e1;
          box-shadow: 0 0 0 3px rgba(203, 213, 225, 0.5);
          flex-shrink: 0;
        }

        .obp-label {
          font-size: 0.78rem;
          font-weight: 600;
          color: #475569;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .obp-step.done .obp-dot {
          background: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
        }

        .obp-step.done .obp-label {
          color: #0f766e;
        }

        .obp-step.active {
          border-color: rgba(124, 58, 237, 0.35);
          background: rgba(124, 58, 237, 0.08);
        }

        .obp-step.active .obp-dot {
          background: #7c3aed;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
        }

        .obp-step.active .obp-label {
          color: #312e81;
        }

        @media (max-width: 900px) {
          .obp-steps {
            grid-template-columns: 1fr;
          }
          .obp-step {
            padding: 0.55rem 0.75rem;
          }
          .obp-label {
            white-space: normal;
            overflow: visible;
            text-overflow: unset;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .obp-fill {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingProgressBar;
